import React, { useState, useEffect, memo, useRef } from 'react';
import { RouteComponentProps } from '@reach/router';
import { RFC, FluxStandardAction as Action } from 'state/StateTypes';
import ViewContainer from 'components/ViewContainer';
import ListHeader from 'components/ListHeader';
import isAuthenticated from 'hooks/isAuthenticated';
import Loading from 'components/Loading';
import { getUsers } from 'state/user/UserActions';
import { User, UserRole } from 'state/user/UserTypes';
import Placeholder from 'components/Placeholder';
import ListItemCustomer from 'components/ListItemCustomer';
import { Error, ErrorCodes } from 'types';
import ErrorMessage from 'components/ErrorMessage';
import uuid from 'uuid';
import { StateItems } from 'state/assets/AssetTypes';
import ListItemAdmin from 'components/ListItemAdmin';
import styled from 'styled-components';
import { scrollPageTo, presentErrorBasedOnCode } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import * as userActions from 'state/users/UsersActionTypes';
import { AppState } from 'state/store';
import * as selectors from 'state/users/UsersSelectors';
import { selectCurrentQuery } from 'state/query/QuerySelectors';
import * as queryActions from 'state/query/QueryActionTypes';

interface Props extends RouteComponentProps {
   role: UserRole;
}

const UserList: RFC<Props> = ({ role }) => {
   //LOCAL
   const [error, setError] = useState<Error | null>(null);
   const [loading, setLoading] = useState<boolean>(true);
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const listScrollRef = useRef<HTMLDivElement>(null);
   const customerRef = useRef<HTMLDivElement>(null);
   //REDUX
   const query = useSelector((state: AppState) => selectCurrentQuery(state));
   const users = useSelector((state: AppState) => selectors.selectUsers(state));
   const isViewingUserDetails = useSelector((state: AppState) => selectors.selectIsViewingUserDetails(state));
   const userIndexBeingViewed = useSelector((state: AppState) => selectors.selectUserIndexBeingViewed(state));
   const totalPages: number = useSelector((state: AppState) => selectors.selectTotalPages(state));
   const totalUsers: number | undefined = useSelector((state: AppState) => selectors.selectTotalUsers(state));
   const dispatch = useDispatch();

   const scrollToRef = () => {
      let pagePosition: number = 0;
      //once the 'load more' button is pressed, scroll to the last user in the list prior to new page of users
      if (!isViewingUserDetails && listScrollRef.current) {
         pagePosition = listScrollRef.current.offsetTop;
      } else if (customerRef.current && !!userIndexBeingViewed) {
         pagePosition = customerRef.current.offsetTop;
      }
      window.scrollTo(0, pagePosition);
   };

   useEffect(() => {
      window.scrollTo(0, 0);
      (async () => {
         if (role !== query.type) {
            return new Promise(resolve => {
               resolve(dispatch({ type: queryActions.RESET_QUERY }));
            }).then(() => dispatch({ type: queryActions.SET_QUERY_TYPE, payload: role }));
         }
      })();
   }, [role, query.sortField, query.sortOrder]);

   const fetchUsersWithQuery = async (): Promise<void> => {
      if (!loading) {
         setLoading(true);
      }
      //fetchedUsers represent users fetched w/ most recent query
      const { users: fetchedUsers, totalPages, totalDocs } = await getUsers({ ...query, type: role });
      const usersToRender: User[] = query.page === 1 ? fetchedUsers : [...users, ...fetchedUsers];
      const actions = [
         dispatch({ type: userActions.SET_TOTAL_PAGES, payload: totalPages }),
         dispatch({ type: userActions.SET_TOTAL_USERS, payload: totalDocs }),
         dispatch({ type: userActions.GET_USERS, payload: usersToRender }),
      ];
      await Promise.all(actions);
      await setLoading(false);
   };

   useEffect(() => {
      (async () => {
         if (!loading) {
            await setLoading(true);
         }
         //only fire query if user is not returning to their search after viewing a user's details
         if (authenticated && !isViewingUserDetails) {
            try {
               await fetchUsersWithQuery();
            } catch (error) {
               //if error results from missing token it will not have a json method
               error = error.json ? await error.json() : error;
               setError(error);
            }
         }
         //only scroll to last user in previous page if it makes sense to
         if (users.length > 21) {
            scrollToRef();
         }
         setLoading(false);
      })();
   }, [query.search, query.sortOrder, query.sortField, query.type, query.page, !!query.search ? query.searchField : undefined, isViewingUserDetails]);

   useEffect(() => {
      //only scroll to last user in previous page if it makes sense to
      if (users.length > 21 && !query.search) {
         scrollToRef();
      }
   }, [users.length]);

   const updateUserList = (updatedUser: User) => {
      dispatch({ type: userActions.UPDATE_USER, payload: updatedUser });
   };

   const loadMoreUsers = async (): Promise<void> => {
      const actions: Action[] = [dispatch({ type: queryActions.SET_QUERY_PAGE, payload: query.page + 1 })];
      //if previously viewing a user's details, when load more is pressed, reset value in redux to allow for query to take place
      if (isViewingUserDetails) {
         actions.push(dispatch({ type: userActions.SET_IS_VIEWING_USER_DETAILS, payload: false }));
      }
      await Promise.all(actions);
   };

   return (
      <ViewContainer>
         <ListHeader
            title={role as StateItems}
            viewingUsersWithRole={role}
            isLoading={loading}
            query={query}
            setParentLoadingState={() => setLoading(true)}
         />
         {!!users.length
            ? users.map((user, index) =>
                 role === 'customer' ? (
                    <ScrollAnchor
                       key={uuid.v4()}
                       ref={index === users.length - 21 ? listScrollRef : null}
                       onClick={() => dispatch({ type: userActions.SET_USER_BEING_VIEWED_INDEX, payload: index })}
                       isFirst={index === 0}>
                       <ListItemCustomer
                          user={user}
                          updateCustomerList={updateUserList}
                          ref={userIndexBeingViewed && userIndexBeingViewed === index ? customerRef : null}
                       />
                    </ScrollAnchor>
                 ) : (
                    <ScrollAnchor
                       key={uuid.v4()}
                       ref={index === users.length - 21 ? listScrollRef : null}
                       onClick={() => dispatch({ type: userActions.SET_USER_BEING_VIEWED_INDEX, payload: index })}
                       isFirst={index === 0}
                       >
                       <ListItemAdmin
                          user={user}
                          updateAdminList={updateUserList}
                          ref={userIndexBeingViewed && userIndexBeingViewed === index ? customerRef : null}
                       />
                    </ScrollAnchor>
                 )
              )
            : !loading && !users.length && !error && <Placeholder content={role} />}
         {!!loading && <Loading />}
         {!!error && (
            <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes) || `Something went wrong while getting ${role} data`}</ErrorMessage>
         )}
         {!loading && !error && query.page < totalPages && (
            <Container>
               <LoadMoreUsers onClick={loadMoreUsers}>
                  Load {totalUsers ? (users.length < totalUsers - 20 ? '20 more users' : `${totalUsers - users.length} more users`) : 'more'}
               </LoadMoreUsers>
            </Container>
         )}
         {users.length >= 25 && <ScrollToTop onClick={() => scrollPageTo('top')}>Scroll to top</ScrollToTop>}
      </ViewContainer>
   );
};

export default memo(UserList);

const ScrollToTop = styled.button.attrs({ type: 'button' })`
   color: ${({ theme }) => theme.primaryBlueLight};
   font-weight: 600;
   margin: 20px 0;
   padding: 0;
   border: none;
   background-color: transparent;
   align-self: center;
   font-size: 14px;
   &:hover {
      color: ${({ theme }) => theme.primaryBlueDark};
      cursor: pointer;
   }
`;

const LoadMoreUsers = styled.button.attrs({ type: 'button' })`
   color: ${({ theme }) => theme.primaryBlueLight};
   border: 1px solid ${({ theme }) => theme.primaryBlueLight};
   background-color: transparent;
   border-radius: 5px;
   padding: 5px 7px;
   font-size: 16px;
   font-weight: 700;

   &:hover {
      cursor: pointer;
      background-color: ${({ theme }) => theme.primaryBlueLight};
      color: ${({ theme }) => theme.primaryWhite};
      transition: 0.2s;
   }
`;

const Container = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   flex-direction: row;
   padding: 10px 15px;
   margin: 0 auto;
`;

const ScrollAnchor = styled.div<{ isFirst: boolean }>`
   margin: 1em auto;
   /* css gods forgive me */
   ${({ isFirst }) => isFirst && 'margin-top: 0'}
`;
